import { Location } from '@wix/ambassador-bookings-services-v2-service/types';
import { groupServicesByCategories } from '@wix/bookings-calendar-catalog-viewer-mapper';
import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { ServiceListSettings } from '../../legacy/appSettings/appSettings';
import { BusinessInfo as LegacyBusinessInfo } from '../../legacy/types';
import { ServiceAvailabilityMap } from '../api/BookingsApi';
import settingsParams from '../components/BookOnline/settingsParams';
import { ServiceListContext } from '../context/createServiceListContext';
import {
  EnrichedService,
  FilterOption,
  ServiceListLayoutOptions,
  ServicesPagingMetadata,
  ViewMode,
} from '../types/types';
import { getFilterAriaLabel } from '../utils/filterOptions/getFilterOptions';
import {
  filterServicesBySelectedTab,
  filterServicesBySettings,
} from '../utils/services/services';
import { isMenuDisplaySelected } from '../utils/settings/settings';
import {
  BodyViewModel,
  createBodyViewModel,
} from './bodyViewModel/bodyViewModel';
import {
  createDialogViewModel,
  DialogViewModel,
} from './dialogViewModel/dialogViewModel';
import {
  createHeaderViewModel,
  HeaderViewModel,
} from './headerViewModel/headerViewModel';
import {
  createServiceImageViewModel,
  ServiceImageViewModel,
} from './serviceImageViewModel/serviceImageViewModel';

export type WidgetViewModel = {
  services: EnrichedService[];
  servicesPagingMetadata: ServicesPagingMetadata;
  businessInfo: LegacyBusinessInfo;
  filterOptions: FilterOption[];
  filterAriaLabel?: string;
  serviceImageViewModel: ServiceImageViewModel;
  headerViewModel?: any;
  bodyViewModel: BodyViewModel;
  seo?: {
    shouldListServicesFromOtherCategories: boolean;
    allServices: EnrichedService[];
  };
  isSEO: boolean;
  serviceListLayout: ServiceListLayoutOptions;
  coursesAvailability?: ServiceAvailabilityMap;
  shouldWorkWithAppSettings: boolean;
  dialogViewModel: DialogViewModel;
  isMultiServiceAppointmentEnabled: boolean;
  errorMessage?: string;
  groupedServicesByCategories?: Map<string, EnrichedService[]>;
  ctaLoadingServiceId?: string;
};

export const createWidgetViewModel = async ({
  flowAPI,
  scale,
  viewMode,
  shouldWorkWithAppSettings,
  allServices,
  servicesPagingMetadata,
  serviceListContext,
  appSettings,
  filterOptions,
}: {
  flowAPI: ControllerFlowAPI;
  scale: number;
  viewMode: ViewMode;
  shouldWorkWithAppSettings: boolean;
  businessLocations: Location[];
  allServices: EnrichedService[];
  servicesPagingMetadata: ServicesPagingMetadata;
  serviceListContext: ServiceListContext;
  appSettings?: ServiceListSettings;
  filterOptions: FilterOption[];
}): Promise<WidgetViewModel> => {
  const {
    settings,
    environment: { isMobile, isEditor },
  } = flowAPI;
  const { businessInfo, isPricingPlanInstalled } = serviceListContext;

  if (shouldWorkWithAppSettings && isEditor) {
    allServices = filterServicesBySettings({
      flowAPI,
      services: allServices,
      shouldWorkWithAppSettings,
      appSettings,
    });
  }

  const filteredServices = filterServicesBySelectedTab({
    filterOptions,
    services: allServices,
    flowAPI,
  });

  const groupedServicesByCategories = isMenuDisplaySelected(
    flowAPI.settings,
    flowAPI.experiments,
  )
    ? groupServicesByCategories(filteredServices)
    : undefined;

  const serviceListLayout = settings.get(settingsParams.serviceListLayout);

  const serviceImageViewModel = createServiceImageViewModel({
    settings,
    isMobile,
    serviceListLayout,
  });

  const bodyViewModel = createBodyViewModel({
    servicesPagingMetadata,
    flowAPI,
    scale,
    imageAspectRatio: serviceImageViewModel.aspectRatio,
    serviceListLayout,
    services: filteredServices,
    isPricingPlanInstalled,
    groupedServicesByCategories,
  });

  const headerViewModel = createHeaderViewModel(flowAPI, filterOptions);

  const shouldListServicesFromOtherCategories =
    viewMode === ViewMode.PAGE &&
    filteredServices.length !== allServices.length;
  const seo = {
    shouldListServicesFromOtherCategories,
    allServices,
  };

  const dialogViewModel = createDialogViewModel();

  return {
    services: filteredServices,
    filterOptions,
    filterAriaLabel: getFilterAriaLabel(flowAPI, appSettings),
    serviceListLayout,
    serviceImageViewModel,
    headerViewModel,
    bodyViewModel,
    businessInfo,
    seo,
    isSEO: flowAPI.environment.isSEO,
    shouldWorkWithAppSettings,
    dialogViewModel,
    isMultiServiceAppointmentEnabled:
      serviceListContext.isMultiServiceAppointmentEnabled,
    servicesPagingMetadata,
    groupedServicesByCategories,
  };
};

export type WidgetErrorStateViewModel = {
  headerViewModel: HeaderViewModel;
  errorText: string;
};

export const createWidgetErrorStateViewModel = ({
  flowAPI,
}: {
  flowAPI: ControllerFlowAPI;
}): WidgetErrorStateViewModel => {
  const {
    translations: { t },
  } = flowAPI;

  const errorStateFilterOptions: FilterOption[] = [];

  const headerViewModel = createHeaderViewModel(
    flowAPI,
    errorStateFilterOptions,
  );

  return {
    headerViewModel,
    errorText: t('error-state.text'),
  };
};
